import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, CaretRight, Play } from '@phosphor-icons/react';
import { createUserRoute } from '../services/api';
import { useCollections } from '../context/CollectionsContext';
import BottomSheet from './BottomSheet';
import PlacePicker from './route-editor/PlacePicker';
import RouteEditor from './route-editor/RouteEditor';
import telegramService from '../services/telegram';
import { findStartStation } from '../utils/metro';
import { plural } from '../utils/plural';
import { notify, notifyIcons } from '../utils/notify';

const PLACES = ['место', 'места', 'мест'];

// Место из избранного — в место маршрута. Этот же вид хранит сервер
// в `stops` своего маршрута
export const toRouteStop = (place) => ({
  placeId: place.id,
  title: place.title,
  latitude: place.latitude,
  longitude: place.longitude,
  metro: place.metro || null,
  address: place.address || null,
  imageUrl: place.images?.[0]?.url || null,
  thumb: place.images?.[0]?.url || null,
  slug: place.slug,
});

// Название по умолчанию: придумывать его до того, как выбраны места,
// было рано — шаг с полем «Название» стоял первым и не пускал дальше
const defaultTitle = () => `Прогулка ${new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`;

/**
 * Мастер своего маршрута: два шага в одной панели.
 *
 * 1. Места — выбор из избранного, номер справа — порядок выбора.
 * 2. Ваш маршрут — карта, длина и время, старт от метро, порядок
 *    перетаскиванием, «Упорядочить по пути», название. Тот же редактор
 *    открывается на прогулке через «⋯ → Изменить маршрут».
 *
 * Раньше шагов было четыре: название, места, старт, порядок — и маршрут
 * собирался вслепую: ни карты, ни расстояния до самого старта прогулки.
 * Теперь две кнопки: «Сохранить» — на потом, «Начать» — прямо сейчас
 */
const CreateRouteWizard = ({ favorites, onClose, onCreated }) => {
  const navigate = useNavigate();
  const { addUserRoute } = useCollections();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(null); // 'save' | 'start'
  const [error, setError] = useState(null);
  const [stops, setStops] = useState([]);
  const [title, setTitle] = useState(defaultTitle);
  const [station, setStation] = useState(null);
  // Станцию выбрали руками — больше не подменяем её ближайшей к первому месту
  const [stationTouched, setStationTouched] = useState(false);

  const togglePlace = (place) => {
    setStops((prev) => (prev.some((s) => s.placeId === place.id)
      ? prev.filter((s) => s.placeId !== place.id)
      : [...prev, toRouteStop(place)]));
  };

  const goToEditor = () => {
    if (stops.length < 2) return;
    telegramService.hapticFeedback('light');
    // Старт по умолчанию — метро у первого места
    if (!stationTouched) {
      const st = findStartStation(stops[0]);
      setStation(st ? { name: st.name, lat: st.lat, lng: st.lng } : null);
    }
    setStep(1);
  };

  const changeStops = (next) => {
    setStops(next);
    if (!stationTouched) {
      const st = findStartStation(next[0]);
      setStation(st ? { name: st.name, lat: st.lat, lng: st.lng } : null);
    }
  };

  const save = async (andStart) => {
    if (stops.length < 2 || saving) return;
    setSaving(andStart ? 'start' : 'save');
    setError(null);
    try {
      const payload = {
        title: title.trim() || defaultTitle(),
        startType: station ? 'metro' : 'geo',
        startMetroName: station?.name || null,
        startMetroLat: station?.lat ?? null,
        startMetroLng: station?.lng ?? null,
        optimized: false,
        stops: stops.map(({ thumb, ...s }, i) => ({ ...s, order: i + 1 })),
      };
      const result = await createUserRoute(payload);
      telegramService.hapticFeedback('success');
      // Счётчик маршрутов в профиле меняется сразу, без перезапроса списка
      addUserRoute(result.data);
      onCreated && onCreated(result.data);
      if (andStart) {
        navigate(`/my-route/${result.data.id}/walk`);
      } else {
        notify('Маршрут сохранён', { icon: notifyIcons.done });
      }
    } catch {
      setError('Не удалось сохранить маршрут. Попробуйте ещё раз');
      telegramService.hapticFeedback('error');
    } finally {
      setSaving(null);
    }
  };

  const handleBack = () => {
    telegramService.hapticFeedback('light');
    if (step > 0) setStep(0);
    else onClose();
  };

  const enoughPlaces = stops.length >= 2;

  return (
    // Визард всегда монтируется родителем по флагу — отдельного isOpen нет.
    // Шаги живут внутри одной панели: стопку листов NN/g не рекомендует
    <BottomSheet
      isOpen
      onClose={onClose}
      title={step === 0 ? 'Выберите места' : 'Ваш маршрут'}
      subtitle={step === 0 ? 'Шаг 1 из 2 · из избранного' : 'Шаг 2 из 2 · проверьте и поправьте'}
      // Выбранные места терять по тапу мимо нельзя
      closeOnBackdrop={false}
      contentClassName="pt-3"
      footer={
        <>
          {error && (
            <div
              className="mb-3 rounded-md px-4 py-3 text-center text-body-sm"
              style={{ background: 'var(--color-danger-dim)', color: 'var(--color-danger-text)' }}
              role="alert"
            >
              {error}
            </div>
          )}
          {step === 0 ? (
            <button
              onClick={goToEditor}
              disabled={!enoughPlaces}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-md border-0 text-label transition-transform duration-fast ease-out active:scale-98"
              style={enoughPlaces
                ? { background: 'var(--color-action)', color: 'var(--color-on-action)' }
                : { background: 'var(--color-card-secondary)', color: 'var(--color-text-secondary)' }}
            >
              {enoughPlaces
                ? <>Далее · {stops.length} {plural(stops.length, PLACES)} <CaretRight size={18} weight="bold" /></>
                : 'Выберите минимум 2 места'}
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleBack}
                aria-label="Назад к выбору мест"
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border-0 bg-theme-card-secondary text-theme-text-primary"
              >
                <CaretLeft size={20} weight="bold" />
              </button>
              <button
                onClick={() => save(false)}
                disabled={!!saving}
                className="flex h-14 flex-1 items-center justify-center rounded-md border-0 bg-theme-card-secondary text-label text-action-text disabled:opacity-60"
              >
                {saving === 'save' ? 'Сохраняем…' : 'Сохранить'}
              </button>
              <button
                onClick={() => save(true)}
                disabled={!!saving}
                className="flex h-14 flex-[1.4] items-center justify-center gap-2 rounded-md border-0 bg-action text-label text-on-action transition-transform duration-fast ease-out active:scale-98 disabled:opacity-60"
              >
                <Play size={18} weight="fill" />
                {saving === 'start' ? 'Открываем…' : 'Начать'}
              </button>
            </div>
          )}
        </>
      }
    >
      {step === 0 ? (
        <PlacePicker
          favorites={favorites}
          selectedIds={stops.map((s) => s.placeId)}
          onToggle={togglePlace}
        />
      ) : (
        <RouteEditor
          stops={stops}
          onChange={changeStops}
          station={station}
          onStationChange={(st) => { setStation(st); setStationTouched(true); }}
          title={title}
          onTitleChange={setTitle}
        />
      )}
    </BottomSheet>
  );
};

export default CreateRouteWizard;
